import { useEffect, useState } from "react";
import {
  ANALYTICS_CONSENT_CHANGED_EVENT,
  analyticsAvailable,
  currentAnalyticsConsentDate,
  getAnalyticsConsent,
  openAnalyticsConsentSettings,
  shouldShowAnalyticsConsentPrompt,
  type AnalyticsConsentState
} from "../analytics";
import { isShowMode } from "../showMode";

export default function DatenschutzPage() {
  const [consent, setConsent] = useState<AnalyticsConsentState | null>(() =>
    isShowMode ? getAnalyticsConsent() : null
  );

  useEffect(() => {
    if (!isShowMode) return;
    const refreshConsent = () => setConsent(getAnalyticsConsent());
    refreshConsent();
    window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, refreshConsent);
    return () => window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, refreshConsent);
  }, []);

  const analyticsChoice = consent
    ? `${consent.decision}${shouldShowAnalyticsConsentPrompt(consent) ? " - renewal needed today" : " for today"}`
    : "not set";

  return (
    <div className="grid" style={{ gap: 12 }}>
      <div className="card">
        <h2 className="section-title" style={{ marginTop: 0 }}>
          Privacy Policy
        </h2>

        <p>
          This privacy policy applies to the published Show web application of the ZeroCO2 interface.
        </p>

        <div style={{ marginTop: 14 }}>
          <strong>1. Controller</strong>
          <div style={{ marginTop: 6, lineHeight: 1.6 }}>
            <div>Marvin Bay</div>
            <div>c/o Online-Impressum.de #6018</div>
            <div>Europaring 90</div>
            <div>53757 Sankt Augustin</div>
            <div>Germany</div>
            <div>
              Email: <a href="mailto:contact@marvinbay.de">contact@marvinbay.de</a>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>2. Purpose and Type of Data Processing</strong>
          <p style={{ marginBottom: 8 }}>
            The web application publicly presents measurement and analysis results in a read-only Show mode.
            It does not provide user accounts, ordering functionality, or an in-app contact form.
          </p>
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>3. Data Processed When Accessing the Site</strong>
          <p>
            When the application is accessed, the hosting provider processes technically necessary log data
            such as IP address, date and time, requested resource, status code, and user agent to provide and
            secure the service.
          </p>
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>4. API and Asset Requests</strong>
          <p>
            To render its content, the application loads data from a connected PostgreSQL/PostgREST interface
            and related static image or HTML assets. Server-side access logs may be created by the
            infrastructure services involved.
          </p>
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>5. Local Browser Storage</strong>
          <p>
            The application may store technically necessary settings locally in the browser, such as display
            or theme settings and the API base URL under the key <code>appSettings</code>. In Show mode, it
            may also store access and analytics consent state locally in the browser, including the consent
            version, the selected decision, a timestamp, and the local date for which the decision applies.
            This data remains on the device used and is not combined into a user profile.
          </p>
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>6. Cookies, Tracking, and Analytics</strong>
          <p>
            The Show web application does not use marketing, tracking, or profiling cookies. If you actively
            consent in the banner, this site uses a self-hosted Umami analytics instance operated on the same
            VPS infrastructure to measure pageviews and a small set of core interactions.
          </p>
          <p>
            Umami is configured as privacy-friendly analytics. It does not set tracking cookies for this
            implementation. The collected data can include the visited page path, referrer, browser, operating
            system, device type, approximate country derived from request metadata, timestamp, and anonymous
            usage events such as opening the Thermoimaging or Thickness views. No filter values, database IDs,
            search text, free-text input, or other personal entries are sent as analytics event data.
          </p>
          <p>
            Analytics only starts after opt-in. If you reject analytics, no Umami script is loaded and no
            analytics request is sent. The application asks for this optional analytics decision once per
            local calendar day. You can change or withdraw your analytics decision at any time below.
          </p>
          {isShowMode && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                className="button secondary"
                onClick={openAnalyticsConsentSettings}
                disabled={!analyticsAvailable()}
                title={analyticsAvailable() ? undefined : "Analytics is not configured for this build."}
              >
                Change analytics settings
              </button>
              <span className="pill">
                Current analytics choice: {analyticsChoice}
              </span>
              <span className="pill">Today: {currentAnalyticsConsentDate()}</span>
            </div>
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>7. Legal Bases</strong>
          <p>
            Processing is based on Art. 6 para. 1 lit. f GDPR, namely the legitimate interest in secure,
            stable, and usable operation of the web application. Where applicable, Art. 6 para. 1 lit. b GDPR
            applies to application-related technical requests. Optional analytics processing is based on your
            consent under Art. 6 para. 1 lit. a GDPR and, where applicable, the relevant consent requirement
            for access to terminal equipment under German TDDDG rules.
          </p>
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>8. Recipients</strong>
          <p>
            Recipients of data may include technical service providers used for hosting, API operation, static
            file delivery, analytics operation, and operational monitoring. The Umami analytics instance is
            self-hosted for this Show application; analytics data is not intentionally transferred to an
            external analytics SaaS provider.
          </p>
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>9. Retention Period</strong>
          <p>
            Server log data is stored only as long as required for operational security, error analysis, abuse
            detection, or legal obligations. Locally stored browser data can be deleted by the user at any
            time. Analytics event data is intended to be retained for no longer than 90 days through a
            server-side cleanup process.
          </p>
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>10. Rights of Data Subjects</strong>
          <p>
            Subject to the statutory requirements, data subjects have rights of access, rectification,
            erasure, restriction of processing, objection, and data portability. They also have the right to
            lodge a complaint with a data protection supervisory authority.
          </p>
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>11. Privacy Contact</strong>
          <div>
            For privacy-related inquiries: <a href="mailto:contact@marvinbay.de">contact@marvinbay.de</a>
          </div>
        </div>
      </div>
    </div>
  );
}
